import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Form, Input, Row, Col, Select, notification, Button, Modal, Tooltip } from 'antd';
import { Store } from 'antd/lib/form/interface';
import componentValues from '../../../../services/utilities/ComponentValue';
import styles from '../../Application/application.module.scss';
import DateOnlyPicker from '../../common/DateOnlyPicker';
import InputBaht from '../../common/InputBaht';
import { API_URL, patchJson, showError } from '../../../../helpers/api/api';
import { datediff } from '../../../../helpers/date/date';
import renderIfHasPermissisons from '../../../../layouts/renderIfHasPermissisons';

function renderOptions(dats: object[]): JSX.Element[] {
  const { Option } = Select;

  return dats.map((option: any) => (
    <Option key={option.name} value={option.value}>
      {option.name}
    </Option>
  ));
}

const Litigation = ({
  loan,
  transactions,
  projectId,
}: {
  loan: any;
  transactions: any;
  projectId: string;
}) => {
  const [form]: any = Form.useForm();
  const { setFieldsValue } = form;
  const intl = useIntl();
  const [litigationStatus, setLitigationStatus] = useState<string | undefined>(
    loan?.litigationStatus,
  );
  const [judgment, setJudgment] = useState<string | undefined>(loan?.judgment);
  const [saving, setSaving] = useState(false);
  const [pendingNulls, setPendingNulls] = useState<Record<string, null>>({});

  const judgmentFields = {
    judgmentDate: null,
    judgmentAmount: null,
    judgmentPrincipal: null,
    judgmentInterest: null,
    judgmentIntRate: null,
    judgmentInstallTerm: null,
    judgmentInstallAmount: null,
    courtFee: null,
    lawyerFee: null,
    enforcementOrderIssueDate: null,
    writOfExecutionIssueDate: null,
    appointExecutingOfficerDate: null,
    assetInvestivateDate: null,
    assetInvestivateResult: null,
    freezeAssetDate: null,
  };

  const onLitigationStatusChange = (value: any) => {
    if (!value) {
      Modal.confirm({
        title: intl.formatMessage({ id: 'litigation.clear-litigation-confirm' }),
        content: intl.formatMessage({ id: 'litigation.clear-litigation-content' }),
        onOk: () => {
          const nulls = {
            litigationStatus: null,
            judgment: null,
            filingDate: null,
            osbCutoffDate: null,
            amountClaim: null,
            court: null,
            blackCaseNo: null,
            negotiationAppointmentDate: null,
            witnessExaminationAppointmentDate: null,
            ...judgmentFields,
          };
          setLitigationStatus(undefined);
          setJudgment(undefined);
          setFieldsValue(nulls);
          setPendingNulls(nulls);
        },
        onCancel: () => setFieldsValue({ litigationStatus }),
      });
    } else {
      setLitigationStatus(value);
      if (value !== 'judgment' && judgment) {
        setJudgment(undefined);
        setFieldsValue({ judgment: null, ...judgmentFields });
      }
    }
  };

  const onJudgmentChange = (value: any) => {
    if (!value) {
      Modal.confirm({
        title: intl.formatMessage({ id: 'litigation.clear-judgment-confirm' }),
        content: intl.formatMessage({ id: 'litigation.clear-judgment-content' }),
        onOk: () => {
          const nulls = { judgment: null, ...judgmentFields };
          setJudgment(undefined);
          setFieldsValue(nulls);
          setPendingNulls(nulls);
        },
        onCancel: () => setFieldsValue({ judgment }),
      });
    } else {
      setJudgment(value);
    }
  };

  const litigationSelect = [
    { name: <FormattedMessage id="litigation.file_lawsuit" />, value: 'file_lawsuit' },
    {
      name: <FormattedMessage id="litigation.negotiation_appointment" />,
      value: 'negotiation_appointment',
    },
    {
      name: <FormattedMessage id="litigation.witness_examination_appointment" />,
      value: 'witness_examination_appointment',
    },
    { name: <FormattedMessage id="litigation.judgment" />, value: 'judgment' },
    {
      name: <FormattedMessage id="litigation.issue_enforcement_order" />,
      value: 'issue_enforcement_order',
    },
    {
      name: <FormattedMessage id="litigation.issue_writ_of_execution" />,
      value: 'issue_writ_of_execution',
    },
    {
      name: <FormattedMessage id="litigation.appoint_executing_officer" />,
      value: 'appoint_executing_officer',
    },
    { name: <FormattedMessage id="litigation.asset_investigate" />, value: 'asset_investigate' },
    { name: <FormattedMessage id="litigation.freeze_asset" />, value: 'freeze_asset' },
  ];

  const judgmentSelect = [
    { name: <FormattedMessage id="litigation.consent_judgment" />, value: 'consent_judgment' },
    { name: <FormattedMessage id="litigation.default_judgment" />, value: 'default_judgment' },
    { name: <FormattedMessage id="litigation.appeal" />, value: 'appeal' },
    { name: <FormattedMessage id="litigation.dismiss" />, value: 'dismiss' },
  ];

  const calculateOsb = (asOfDate: string, intRate: number, lastTransaction: any) => {
    const { cfPrincipal, cfInterest, cfFee } = lastTransaction;
    const principal = cfPrincipal / 100;
    const duration = datediff(asOfDate, lastTransaction.transDate);
    const dueInterest = ((cfPrincipal * intRate) / 365) * duration;
    const interest = (cfInterest + dueInterest) / 100;
    const fee = cfFee / 100;
    const total = principal + interest + fee;
    return {
      asOfDate,
      total: (total * 100).toFixed(0),
      principal: (principal * 100).toFixed(0),
      interest: (interest * 100).toFixed(0),
      fee: (fee * 100).toFixed(0),
    };
  };

  const onOsbCutoffDateChange = (cutoffDate: any) => {
    const osb = calculateOsb(cutoffDate, loan.intRate, transactions[0]);
    const { total } = osb;
    const currentAmountClaim = form.getFieldValue('amountClaim');

    if (currentAmountClaim && String(currentAmountClaim) !== String(total)) {
      Modal.confirm({
        title: intl.formatMessage({ id: 'litigation.amountClaim-recalculate-confirm' }),
        content: intl.formatMessage({ id: 'litigation.amountClaim-recalculate-content' }),
        onOk: () => setFieldsValue({ amountClaim: total }),
      });
    } else {
      setFieldsValue({ amountClaim: total });
    }
  };

  const requiredWhen = (condition: boolean) => ({
    required: condition,
    validator(_: any, value: any) {
      if (condition && (value == null || value === '' || value === 'Invalid date')) {
        return Promise.reject(new Error(' '));
      }
      return Promise.resolve();
    },
  });

  const sanitize = (values: Record<string, any>) =>
    Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, v === 'Invalid date' || v === '' ? null : v]),
    );

  const onFinish = async (value: Store) => {
    setSaving(true);
    try {
      await patchJson(`${API_URL}/projects/${projectId}/loans/${loan.id}`, {
        ...sanitize(value),
        ...pendingNulls,
      });
      setPendingNulls({});
      notification.success({ message: intl.formatMessage({ id: 'litigation.success-message' }) });
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const SaveButton = renderIfHasPermissisons(
    'loans.*.update',
    <Button type="primary" htmlType="submit" loading={saving}>
      <FormattedMessage id="save" />
    </Button>,
  );

  return (
    <div>
      <Form form={form} name="filing" initialValues={loan} layout="vertical" onFinish={onFinish}>
        <Row gutter={componentValues.gutterValue}>
          <Col
            sm={{ span: componentValues.fullColSpan }}
            md={{ span: componentValues.halfColSpan }}
          >
            <Form.Item
              label={<FormattedMessage id="litigation.litigationStatus" />}
              name="litigationStatus"
            >
              <Select allowClear onChange={onLitigationStatusChange}>
                {renderOptions(litigationSelect)}
              </Select>
            </Form.Item>
          </Col>
          <Col
            sm={{ span: componentValues.fullColSpan }}
            md={{ span: componentValues.halfColSpan }}
          >
            <Form.Item label={<FormattedMessage id="judgment" />} name="judgment">
              <Select
                allowClear
                disabled={litigationStatus !== 'judgment'}
                onChange={onJudgmentChange}
              >
                {renderOptions(judgmentSelect)}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {!!litigationStatus && (
          <>
            <strong className={styles.title}>{<FormattedMessage id="litigation.filing" />}</strong>
            <Row gutter={componentValues.gutterValue}>
              <Col
                sm={{ span: componentValues.fullColSpan }}
                md={{ span: componentValues.halfColSpan }}
              >
                <Form.Item
                  label={<FormattedMessage id="litigation.filingDate" />}
                  name="filingDate"
                  rules={[requiredWhen(!!litigationStatus)]}
                >
                  <DateOnlyPicker format="DD-MM-YYYY" />
                </Form.Item>
              </Col>
              <Col
                sm={{ span: componentValues.fullColSpan }}
                md={{ span: componentValues.halfColSpan }}
              >
                <Form.Item
                  label={<FormattedMessage id="litigation.osbCutoffDate" />}
                  name="osbCutoffDate"
                  rules={[requiredWhen(!!litigationStatus)]}
                >
                  <DateOnlyPicker format="DD-MM-YYYY" onChange={onOsbCutoffDateChange} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={componentValues.gutterValue}>
              <Col
                sm={{ span: componentValues.fullColSpan }}
                md={{ span: componentValues.halfColSpan }}
              >
                <Form.Item
                  label={
                    <Tooltip title={intl.formatMessage({ id: 'litigation.amountClaim-tooltip' })}>
                      <span>
                        <FormattedMessage id="litigation.amountClaim" /> &#9432;
                      </span>
                    </Tooltip>
                  }
                  name="amountClaim"
                  rules={[requiredWhen(!!litigationStatus)]}
                >
                  <InputBaht />
                </Form.Item>
              </Col>
              <Col
                sm={{ span: componentValues.fullColSpan }}
                md={{ span: componentValues.halfColSpan }}
              >
                <Form.Item label={<FormattedMessage id="litigation.court" />} name="court">
                  <Input />
                </Form.Item>
              </Col>
              <Col
                sm={{ span: componentValues.fullColSpan }}
                md={{ span: componentValues.halfColSpan }}
              >
                <Form.Item label={<FormattedMessage id="blackCaseNo" />} name="blackCaseNo">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <strong className={styles.title}>
              {<FormattedMessage id="litigation.negotiationAppointment" />}
            </strong>
            <Row gutter={componentValues.gutterValue}>
              <Col
                sm={{ span: componentValues.fullColSpan }}
                md={{ span: componentValues.halfColSpan }}
              >
                <Form.Item
                  label={<FormattedMessage id="litigation.negotiationAppointmentDate" />}
                  name="negotiationAppointmentDate"
                >
                  <DateOnlyPicker format="DD-MM-YYYY" />
                </Form.Item>
              </Col>
            </Row>

            <strong className={styles.title}>
              {<FormattedMessage id="litigation.witnessExaminationAppointment" />}
            </strong>
            <Row gutter={componentValues.gutterValue}>
              <Col
                sm={{ span: componentValues.fullColSpan }}
                md={{ span: componentValues.halfColSpan }}
              >
                <Form.Item
                  label={<FormattedMessage id="litigation.witnessExaminationAppointmentDate" />}
                  name="witnessExaminationAppointmentDate"
                >
                  <DateOnlyPicker format="DD-MM-YYYY" />
                </Form.Item>
              </Col>
            </Row>

            {!!judgment && (
              <>
                <strong className={styles.title}>
                  {<FormattedMessage id="litigation.judgments" />}
                </strong>
                <Row gutter={componentValues.gutterValue}>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.judgmentDate" />}
                      name="judgmentDate"
                    >
                      <DateOnlyPicker format="DD-MM-YYYY" />
                    </Form.Item>
                  </Col>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.judgmentAmount" />}
                      name="judgmentAmount"
                    >
                      <InputBaht />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={componentValues.gutterValue}>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.judgmentPrincipal" />}
                      name="judgmentPrincipal"
                    >
                      <InputBaht />
                    </Form.Item>
                  </Col>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.judgmentInterest" />}
                      name="judgmentInterest"
                    >
                      <InputBaht />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={componentValues.gutterValue}>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.judgmentIntRate" />}
                      name="judgmentIntRate"
                    >
                      <InputBaht />
                    </Form.Item>
                  </Col>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.judgmentInstallTerm" />}
                      name="judgmentInstallTerm"
                    >
                      <InputBaht />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={componentValues.gutterValue}>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.judgmentInstallAmount" />}
                      name="judgmentInstallAmount"
                    >
                      <InputBaht />
                    </Form.Item>
                  </Col>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.courtFee" />}
                      name="courtFee"
                    >
                      <InputBaht />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={componentValues.gutterValue}>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.lawyerFee" />}
                      name="lawyerFee"
                    >
                      <InputBaht />
                    </Form.Item>
                  </Col>
                </Row>

                <strong className={styles.title}>
                  {<FormattedMessage id="litigation.enforcementOrderIssue" />}
                </strong>
                <Row gutter={componentValues.gutterValue}>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.enforcementOrderIssueDate" />}
                      name="enforcementOrderIssueDate"
                    >
                      <DateOnlyPicker format="DD-MM-YYYY" />
                    </Form.Item>
                  </Col>
                </Row>

                <strong className={styles.title}>
                  {<FormattedMessage id="litigation.writOfExecutionIssue" />}
                </strong>
                <Row gutter={componentValues.gutterValue}>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.writOfExecutionIssueDate" />}
                      name="writOfExecutionIssueDate"
                    >
                      <DateOnlyPicker format="DD-MM-YYYY" />
                    </Form.Item>
                  </Col>
                </Row>

                <strong className={styles.title}>
                  {<FormattedMessage id="litigation.appointExecutingOfficer" />}
                </strong>
                <Row gutter={componentValues.gutterValue}>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.appointExecutingOfficerDate" />}
                      name="appointExecutingOfficerDate"
                    >
                      <DateOnlyPicker format="DD-MM-YYYY" />
                    </Form.Item>
                  </Col>
                </Row>

                <strong className={styles.title}>
                  {<FormattedMessage id="litigation.assetInvestivate" />}
                </strong>
                <Row gutter={componentValues.gutterValue}>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.assetInvestivateDate" />}
                      name="assetInvestivateDate"
                    >
                      <DateOnlyPicker format="DD-MM-YYYY" />
                    </Form.Item>
                  </Col>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.assetInvestivateResult" />}
                      name="assetInvestivateResult"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <strong className={styles.title}>
                  {<FormattedMessage id="litigation.freezeAsset" />}
                </strong>
                <Row gutter={componentValues.gutterValue}>
                  <Col
                    sm={{ span: componentValues.fullColSpan }}
                    md={{ span: componentValues.halfColSpan }}
                  >
                    <Form.Item
                      label={<FormattedMessage id="litigation.freezeAssetDate" />}
                      name="freezeAssetDate"
                    >
                      <DateOnlyPicker format="DD-MM-YYYY" />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </>
        )}

        <Row
          style={{
            position: 'sticky',
            bottom: 0,
            background: '#fff',
            padding: '12px 0',
            zIndex: 10,
          }}
        >
          <Col span={24}>
            <SaveButton />
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Litigation;
